<template>
    <div>
        <form v-loading="loading" @submit.prevent="save">
            <div class="card mx-auto">
                <h4 class="card__title">{{ title }}</h4>

                <agreement-texts
                    v-if="product && product.agreement_texts"
                    :product="product"
                />

                <p>
                    Skicka faktura när leveransstatus för adresser är:
                </p>

                <select-input
                    v-model="action"
                    :options="statuses"
                    :opt-out-sorting="true"
                    class="mb-0"
                />

                <billecta-invoice-trigger-form-articles
                    title="Lägg till Billecta-artiklar för installation"
                    :initial-articles="directArticles"
                    :initial-num-months="0"
                    type="direct"
                    @updateArticles="updateDirectArticles($event)"
                />

                <billecta-invoice-trigger-form-articles
                    title="Lägg till Billecta-artiklar för avbetalning"
                    :initial-articles="monthlyArticles"
                    :initial-num-months="numMonths"
                    type="monthly"
                    @updateArticles="updateMonthlyArticles($event)"
                    @updateNumMonths="updateNumMonths($event)"
                />
            </div>

            <div class="flex max-w-md mx-auto justify-between">
                <a
                    class="btn is-ghost btn-lg"
                    :href="backUrl"
                >
                    Avbryt
                </a>

                <button
                    class="btn is-primary btn-lg w-full md:w-auto"
                    type="submit"
                >
                    Spara
                </button>
            </div>
        </form>
    </div>
</template>

<script>
    import AgreementTexts from './AgreementTexts';
    import BillectaInvoiceTriggerFormArticles from './BillectaInvoiceTriggerFormArticles';

    export default {
        components: {
            BillectaInvoiceTriggerFormArticles,
            AgreementTexts,
        },
        props: {
            title: {
                type: String,
                required: true,
            },
            product: {
                type: Object,
                required: true,
            },
            invoiceTrigger: {
                type: Object,
                required: false,
                default: null,
            },
            initialDirectArticles: {
                type: Array,
                required: true,
            },
            initialMonthlyArticles: {
                type: Array,
                required: true,
            },
            initialNumMonths: {
                type: Number,
                required: true,
            },
        },

        data() {
            return {
                houseworkType: 'rot',
                action: 'dig',
                statuses: {
                    dig: 'Grävd',
                },
                directArticles: JSON.parse(JSON.stringify(this.initialDirectArticles)),
                monthlyArticles: JSON.parse(JSON.stringify(this.initialMonthlyArticles)),
                updatedDirectArticles: [],
                updatedMonthlyArticles: [],
                numMonths: this.initialNumMonths,
                errors: {},
                loading: false,
            };
        },

        computed: {
            requestRoute() {
                return this.invoiceTrigger
                    ? route('api.products.invoice-triggers.update', [this.product, this.invoiceTrigger])
                    : route('api.products.invoice-triggers.store', this.product);
            },
            requestMethod() {
                return this.invoiceTrigger ? 'patch' : 'post';
            },
            checkedArticles() {
                return this.articles
                    .filter(article => article.is_checked)
                    .map(article => {
                        return {
                            fortnox_article_id: article.fortnox_article_id,
                            deduction: article.deduction,
                            price: article.price,
                        };
                    });
            },
            mostExpensiveArticle() {
                return this.articles.reduce((max, article) => {
                    return (article.is_checked && article.is_housework) && article.price >= max.price ? article : max;
                }, { price: null });
            },
            canUseTaxReduction() {
                return this.mostExpensiveArticle.price !== null;
            },
            taxReduction() {
                const checkedArticles = this.checkedArticles;

                const checkedRotArticles = checkedArticles.filter(article => article.deduction === 'rot');
                const checkedRutArticles = checkedArticles.filter(article => article.deduction === 'rut');

                const rotArticleTotalPrice = checkedRotArticles.reduce((acc, article) => acc + Number(article.price), 0) * 1.25;
                const rutArticleTotalPrice = checkedRutArticles.reduce((acc, article) => acc + Number(article.price), 0) * 1.25;

                return Math.round(rotArticleTotalPrice * .3 + rutArticleTotalPrice * .5);
            },
            helpText() {
                return `${this.houseworkType.toUpperCase()} beräknas på artikeln som innebär husarbete
                (inkl. moms) * ${this.houseworkType === 'rot' ? '0,3' : '0,5'}. Finns det flera artiklar med husarbete
                för detta fakturatillfälle beräknas ${this.houseworkType.toUpperCase()} på den dyraste artikeln av dessa.`;
            },
            total() {
                return Math.round(this.checkedArticles.reduce((a, b) => +a + +b.price, 0));
            },
            VAT() {
                return Math.round(this.total * 0.25);
            },
            totalToPay() {
                return Math.round((this.total + this.VAT) - this.taxReduction);
            },
            backUrl() {
                return route('admin.products.show', this.product);
            },
        },

        created() {
            if (this.invoiceTrigger) {
                this.houseworkType = this.invoiceTrigger.housework_type;
                this.action = this.invoiceTrigger.action;
            }
        },

        methods: {
            async save() {
                this.loading = true;

                try {
                    await axios({
                        method: this.requestMethod,
                        url: this.requestRoute,
                        data: {
                            action: this.action,
                            articles: [...this.updatedDirectArticles, ...this.updatedMonthlyArticles],
                            financial_period_length: this.numMonths,
                            housework_type: this.houseworkType,
                            provider: 'billecta',
                        },
                    });
                    window.location.href = this.backUrl;
                } catch (error) {
                    if (error.response.status === 422) {
                        flash_error('Formuläret är inte korrekt ifyllt');
                        this.errors = error.response.data.errors;
                    } else {
                        flash_error('Faktureringstillfället kunde inte sparas.');
                    }

                    this.loading = false;
                }
            },

            getArticleErrorMessage(index, key) {
                if (this.errors && this.errors[`articles.${index}.${key}`]) {
                    return this.errors[`articles.${index}.${key}`][0];
                }
            },

            updateDirectArticles(articles) {
                this.updatedDirectArticles = articles;
            },

            updateMonthlyArticles(articles) {
                this.updatedMonthlyArticles = articles;
            },

            updateNumMonths(numMonths) {
                this.numMonths = numMonths;
            },
        },
    };
</script>
